import React from "react";
import {
  ButtonPdfContainerStyled,
  ButtonStyled,
  EquipmentContainter,
  EquipmentListContainter,
  InputEquipmentContainer,
} from "./styles";
import { MenuItem, Typography } from "@mui/material";
import StyledTextField from "../StyledTextField";
import { PictureAsPdfSharp } from "@mui/icons-material";
import EquipmentListAccordion from "../EquipmentListAccordion";

interface EquipmentTabProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (e: any) => void;
  handleBlur: (e: any) => void;
  setFieldValue: (field: string, value: any) => void;
  employees: any[];
  currentCustomer?: any;
  onDeleteEquipment: (id: number) => void;
  disabled?: boolean;
}

const EquipmentTab = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  employees,
  currentCustomer,
  onDeleteEquipment,
  disabled = false,
}: EquipmentTabProps) => {

  const getLimitedFileName = (name: string, maxLength = 15) => {
    if (!name) return "Anexar PDF";
    if (name.length <= maxLength) return name;
    const extIndex = name.lastIndexOf(".");
    const ext = extIndex !== -1 ? name.substring(extIndex) : "";
    const base = name.substring(0, maxLength - ext.length - 3);
    return base + "..." + ext;
  };
  return (
    <EquipmentContainter>
      <Typography variant="h6" mt={2}>
        Novo Equipamento
      </Typography>

      <InputEquipmentContainer>
        <StyledTextField
          label="Equipamento"
          name="equipmentName"
          value={values.equipmentName}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          error={touched.equipmentName && Boolean(errors.equipmentName)}
          helperText={touched.equipmentName && (errors.equipmentName as string)}
        />
        <StyledTextField
          label="Modelo"
          name="model"
          value={values.model}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          error={touched.model && Boolean(errors.model)}
          helperText={touched.model && (errors.model as string)}
        />
        <StyledTextField
          label="Nº Série"
          name="serialNumber"
          value={values.serialNumber}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          error={touched.serialNumber && Boolean(errors.serialNumber)}
          helperText={touched.serialNumber && (errors.serialNumber as string)}
        />
        <StyledTextField
          label="Marca"
          name="brand"
          value={values.brand}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          error={touched.brand && Boolean(errors.brand)}
          helperText={touched.brand && (errors.brand as string)}
        />
      </InputEquipmentContainer>

      <InputEquipmentContainer
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 2,
        }}
      >
        <StyledTextField
          select
          label="Técnico"
          name="employee_id"
          value={values.employee_id || ""}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          error={touched.employee_id && Boolean(errors.employee_id)}
          sx={{
            width: "100%",
            maxWidth: "250px",
            "& .MuiOutlinedInput-root": {
              overflow: "visible",
            },
            "& .MuiOutlinedInput-input": {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              height: "auto",
            },
          }}
          helperText={touched.employee_id && (errors.employee_id as string)}
        >
          {employees
            .sort((a, b) => (a.active === b.active ? 0 : a.active ? -1 : 1))
            .map((emp) => (
              <MenuItem
                key={emp.id}
                value={emp.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  color: emp.active ? "inherit" : "error.main",
                  textTransform: 'uppercase',
                  fontSize: 12,
                }}
              >
                {emp.name}
                <Typography
                  variant="caption"
                  sx={{
                    ml: 2,
                    fontWeight: "bold",
                    color: emp.active ? "#2e7d32" : "#d32f2f",
                    textTransform: 'capitalize',
                    fontSize: 10
                  }}
                >
                  {emp.active ? "Ativo" : "Inativo"}
                </Typography>
              </MenuItem>
            ))}
        </StyledTextField>
        <StyledTextField
          label="Dt. Calibração"
          name="calibrationDate"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={values.calibrationDate}
          onChange={(e) => setFieldValue("calibrationDate", e.target.value)}
          disabled={disabled}
          error={touched.calibrationDate && Boolean(errors.calibrationDate)}
          helperText={
            touched.calibrationDate && (errors.calibrationDate as string)
          }
        />

        <StyledTextField
          label="Dt. Vencimento"
          name="expiration_date"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={values.expiration_date}
          onChange={(e) => setFieldValue("expiration_date", e.target.value)}
          disabled={disabled}
          error={touched.expiration_date && Boolean(errors.expiration_date)}
          helperText={
            touched.expiration_date && (errors.expiration_date as string)
          }
          sx={{
            "& .MuiFormHelperText-root": {
              whiteSpace: "normal",
              lineHeight: 1,
              fontSize: "11px",
              marginBottom: "-2px",
            },
          }}
        />

        <input
          type="file"
          id="pdf-upload"
          style={{ display: "none" }}
          accept="application/pdf"
          disabled={disabled}
          onChange={(e) => {
            const file = e.currentTarget.files?.[0];
            setFieldValue("pdf_file", file || null);
          }}
        />

        <ButtonPdfContainerStyled>
          <ButtonStyled
            type="button"
            variant="contained"
            startIcon={<PictureAsPdfSharp />}
            onClick={() => document.getElementById("pdf-upload")?.click()}
            disabled={disabled}
          >
            {values.pdf_file ? getLimitedFileName(values.pdf_file.name) : "Anexar PDF"}
          </ButtonStyled>
          <Typography variant="caption" className="error-text">
            {touched.pdf_file && errors.pdf_file
              ? (errors.pdf_file as string)
              : ""}
          </Typography>
        </ButtonPdfContainerStyled>
      </InputEquipmentContainer>

      <EquipmentListContainter>
        {currentCustomer?.equipment?.length > 0 && (
          <EquipmentListAccordion
            equipments={currentCustomer.equipment}
            onDelete={onDeleteEquipment}
          />
        )}
      </EquipmentListContainter>
    </EquipmentContainter>
  );
};

export default EquipmentTab;
